import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination
} from '@mui/material'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'

const SOURCES = [
  'CASHBACK_MONTHLY',
  'CASHBACK_FUEL',
  'CASHBACK_ELECTRIC',
  'REFERRAL_BONUS',
  'ADJUSTMENT',
  'PAYOUT'
]

const CURRENCIES = ['EUR', 'USD', 'GBP']

export default function AdminWallet() {
  const [items, setItems] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)

  const [sort, setSort] = useState('userId')
  const [dir, setDir] = useState('ASC')

  const [filters, setFilters] = useState({
    id: '',
    userId: '',
    amount: '',
    currency: '',
    source: '',
    description: '',
    month: '',
    createdAt: '',
    updatedAt: '',
    operation_date: ''
  })

  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const qs = useMemo(() => {
    const p = new URLSearchParams()
    p.set('limit', String(rowsPerPage))
    p.set('offset', String(page * rowsPerPage))
    p.set('sort', sort || 'userId')
    p.set('dir', dir || 'ASC')

    Object.entries(filters).forEach(([k, v]) => {
      if (v) p.set(k, String(v))
    })

    return p.toString()
  }, [page, rowsPerPage, sort, dir, filters])

  const load = useCallback(async () => {
    const res = await fetchWithAuth(`/admin/wallet-entries?${qs}`)

    if (!res.ok) {
      console.error('wallet-entries load failed:', res.status)
      setItems([])
      setTotal(0)
      return
    }

    const data = await res.json()

    const rows = Array.isArray(data?.rows)
      ? data.rows
      : Array.isArray(data)
        ? data
        : []

    setItems(rows)
    setTotal(Number(data?.count ?? rows.length) || 0)
  }, [qs])

  useEffect(() => {
    load()
  }, [load])

  const onHeaderClick = (col) => {
    if (sort === col) {
      setDir(d => (d === 'ASC' ? 'DESC' : 'ASC'))
    } else {
      setSort(col)
      setDir('ASC')
    }
  }

  const openEdit = (row) => {
    setEditing({
      id: row.id,
      amount: row.amount,
      currency: row.currency,
      source: row.source,
      description: row.description,
      month: row.month || '',
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      operation_date: row.operation_date
    })

    setEditOpen(true)
  }

  const saveEdit = async () => {
    const res = await fetchWithAuth(`/admin/wallet-entries/${editing.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editing)
    })

    if (res.ok) {
      setEditOpen(false)
      setEditing(null)
      load()
    } else {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Chyba pri ukladaní')
    }
  }

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: 1
        }}
      >
        <h3
          style={{
            fontSize: 16,
            margin: 0
          }}
        >
          Wallet – položky
        </h3>

        <Typography
          sx={{
            fontSize: 13,
            opacity: 0.7
          }}
        >
          Záznamov: {total}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label="ID"
          size="small"
          value={filters.id}
          onChange={e => setFilters(f => ({ ...f, id: e.target.value }))}
          sx={{ width: 140 }}
        />

        <TextField
          label="User ID"
          size="small"
          value={filters.userId}
          onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))}
          sx={{ width: 140 }}
        />

        <TextField
          label="Suma"
          size="small"
          value={filters.amount}
          onChange={e => setFilters(f => ({ ...f, amount: e.target.value }))}
          sx={{ width: 140 }}
        />

        <TextField
          select
          label="Mena"
          size="small"
          value={filters.currency}
          onChange={e => setFilters(f => ({ ...f, currency: e.target.value }))}
          sx={{ width: 140 }}
        >
          <MenuItem value="">(všetko)</MenuItem>

          {CURRENCIES.map(c => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Zdroj"
          size="small"
          value={filters.source}
          onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}
          sx={{ width: 220 }}
        >
          <MenuItem value="">(všetko)</MenuItem>

          {SOURCES.map(s => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Popis"
          size="small"
          value={filters.description}
          onChange={e =>
            setFilters(f => ({ ...f, description: e.target.value }))
          }
          sx={{ width: 180 }}
        />

        <TextField
          label="Mesiac"
          type="month"
          size="small"
          value={filters.month}
          onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}
        />

        <TextField
          label="Vytvorené"
          type="datetime-local"
          size="small"
          value={filters.createdAt}
          onChange={e =>
            setFilters(f => ({ ...f, createdAt: e.target.value }))
          }
        />

        <TextField
          label="Upravené"
          type="datetime-local"
          size="small"
          value={filters.updatedAt}
          onChange={e =>
            setFilters(f => ({ ...f, updatedAt: e.target.value }))
          }
        />

        <TextField
          label="Zapísané"
          type="datetime-local"
          size="small"
          value={filters.operation_date}
          onChange={e =>
            setFilters(f => ({ ...f, operation_date: e.target.value }))
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setPage(0)
            load()
          }}
        >
          Hľadať
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #c7c7c7',
          borderRadius: '10px',
          boxShadow: 'none'
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1600,

            '& thead th': {
              fontWeight: 'bold',
              backgroundColor: '#c8e1e1',
              whiteSpace: 'nowrap',
              fontSize: 12,
              padding: '6px 8px'
            },

            '& tbody td': {
              fontSize: 12,
              padding: '5px 8px',
              whiteSpace: 'nowrap'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Upraviť</TableCell>

              {[
                'id',
                'userId',
                'membership',
                'amount',
                'currency',
                'source',
                'description',
                'month',
                'createdAt',
                'updatedAt',
                'operation_date'
              ].map(col => (
                <TableCell
                  key={col}
                  onClick={() => onHeaderClick(col)}
                  sx={{ cursor: 'pointer' }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map(r => {
              const membershipType =
                r?.user?.membership?.membershipType?.type || '-'

              return (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <EditButton
                      onClick={() => openEdit(r)}
                    />
                  </TableCell>

                  <TableCell>{r.id}</TableCell>
                  <TableCell>{r.userId}</TableCell>
                  <TableCell>{membershipType}</TableCell>

                  <TableCell>
                    {typeof r.amount === 'number'
                      ? r.amount.toFixed(2)
                      : r.amount}
                  </TableCell>

                  <TableCell>{r.currency || 'EUR'}</TableCell>
                  <TableCell>{r.source}</TableCell>
                  <TableCell>{r.description || '-'}</TableCell>
                  <TableCell>{r.month || '-'}</TableCell>

                  <TableCell>
                    {r.createdAt
                      ? new Date(r.createdAt).toLocaleString('sk-SK')
                      : '-'}
                  </TableCell>

                  <TableCell>
                    {r.updatedAt
                      ? new Date(r.updatedAt).toLocaleString('sk-SK')
                      : '-'}
                  </TableCell>

                  <TableCell>
                    {r.operation_date
                      ? new Date(r.operation_date).toLocaleString('sk-SK')
                      : '-'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Na stránku"
        />
      </Box>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
      >
        <DialogTitle>
          Upraviť položku
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1,
            minWidth: 360
          }}
        >
          <TextField
            label="Suma"
            type="number"
            value={editing?.amount ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                amount: Number(e.target.value)
              }))
            }
          />

          <TextField
            select
            label="Mena"
            value={editing?.currency ?? 'EUR'}
            onChange={e =>
              setEditing(v => ({
                ...v,
                currency: e.target.value
              }))
            }
          >
            {CURRENCIES.map(c => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Zdroj"
            value={editing?.source ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                source: e.target.value
              }))
            }
          >
            {SOURCES.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Popis"
            value={editing?.description ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                description: e.target.value
              }))
            }
          />

          <TextField
            label="Mesiac (YYYY-MM)"
            type="month"
            value={editing?.month ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                month: e.target.value
              }))
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>
            Zrušiť
          </Button>

          <Button
            onClick={saveEdit}
            variant="contained"
          >
            Uložiť
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}